import {
  Icon,
  ICON_SIZES,
  type IconName,
  MixpanelEvent,
  MixpanelEventSource,
  Popover,
  Text,
  useMixpanel,
  usePopoverState,
} from '@talos/kyoko';
import type { CSSProperties } from 'react';
import { useCallback } from 'react';
import { useDisplaySettings } from '../../providers/DisplaySettingsProvider';
import type { NavigationItem } from '../../tokens/navigationPaths';
import { SidebarNavItemIcon, SidebarNavItemLabel, SidebarNavItemWrapper, SidebarSubmenuIndicator } from './styles';

const SIDEBAR_ITEM_CONTENT_STYLE: CSSProperties = {
  pointerEvents: 'none',
};
export function SidebarNavItem({ item }: { item: NavigationItem }) {
  const { showSidebarLabels } = useDisplaySettings();
  const popover = usePopoverState({ trigger: 'hover', placement: 'right', usePortal: true, delay: 200 });
  const mixpanel = useMixpanel();
  const handleClick = useCallback(() => {
    popover.close();
    if (item.mixpanelEvent) {
      mixpanel.track(MixpanelEvent.NavigateTo, { path: item.path, source: MixpanelEventSource.NavigationSidebar });
      mixpanel.track(item.mixpanelEvent);
    }
  }, [popover, item.mixpanelEvent, mixpanel, item.path]);

  return showSidebarLabels ? (
    <SidebarNavItemWrapper onClick={handleClick} data-testid="sidebar-nav-item" to={item.path || ''}>
      <NavItemIcon small={true} icon={item.icon} hasChildren={!!item.children} />
      <Popover
        {...popover}
        isOpen={item.shortLabel ? popover.isOpen : false}
        isSmall
        contentStyle={SIDEBAR_ITEM_CONTENT_STYLE}
      >
        <SidebarNavItemLabel>{item.shortLabel ?? item.label}</SidebarNavItemLabel>
        <Text color="colorTextImportant">{item.label}</Text>
      </Popover>
    </SidebarNavItemWrapper>
  ) : (
    <Popover {...popover} isSmall contentStyle={SIDEBAR_ITEM_CONTENT_STYLE}>
      <SidebarNavItemWrapper onClick={handleClick} data-testid="sidebar-nav-item" to={item.path || ''}>
        <NavItemIcon small={false} icon={item.icon} hasChildren={!!item.children} />
      </SidebarNavItemWrapper>
      <Text color="colorTextImportant">{item.label}</Text>
    </Popover>
  );
}

function NavItemIcon({ icon, hasChildren, small }: { icon?: IconName; hasChildren?: boolean; small: boolean }) {
  return (
    <SidebarNavItemIcon small={small}>
      {icon && <Icon icon={icon} size={small ? 14 : ICON_SIZES.MEDIUM} />}
      {hasChildren && (
        <SidebarSubmenuIndicator width="5" height="5" viewBox="0 0 5 5">
          <path
            d="M2.66 0.22C3.13 -0.25 3.94 0.08 3.94 0.75V3.19C3.94 3.6 3.6 3.94 3.19 3.94H0.75C0.08 3.94 -0.25 3.13 0.22 2.66L2.66 0.22Z"
            fill="currentColor"
          />
        </SidebarSubmenuIndicator>
      )}
    </SidebarNavItemIcon>
  );
}
